const RING_MAX = 2000;
const DB_NAME = 'mentor_api_log_v1';
const DB_STORE = 'entries';
const FLUSH_INTERVAL_MS = 30_000;
const FLUSH_BATCH_MAX = 100;

const SENSITIVE_QUERY_KEYS = new Set(['password', 'token', 'authorization']);

let sessionId = null;
let sequence = 0;
let ring = [];
let context = {
  userRole: null,
  userId: null,
  activeTab: null,
  callerTag: null,
};
let listeners = new Set();
let flushHandler = null;
let flushTimer = null;
let dbPromise = null;
let idbReady = false;

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `log-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function parseHost(baseUrl) {
  try {
    return new URL(baseUrl).host;
  } catch {
    return String(baseUrl || 'unknown');
  }
}

function splitPathQuery(path) {
  const raw = String(path || '');
  const qIndex = raw.indexOf('?');
  if (qIndex === -1) return { path: raw, queryKeys: [] };
  const pathname = raw.slice(0, qIndex);
  const params = new URLSearchParams(raw.slice(qIndex + 1));
  const queryKeys = [];
  params.forEach((value, key) => {
    if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) return;
    queryKeys.push(key);
  });
  return { path: pathname, queryKeys };
}

function captureCallerStack() {
  const lines = (new Error().stack || '').split('\n').slice(2, 8);
  return lines.join('\n').trim() || null;
}

function notify() {
  listeners.forEach((fn) => {
    try { fn(ring); } catch { /* ignore */ }
  });
}

function openDb() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    dbPromise = Promise.resolve(null);
    return dbPromise;
  }
  dbPromise = new Promise((resolve) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        const store = db.createObjectStore(DB_STORE, { keyPath: 'id' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('flushed', 'flushed', { unique: false });
      }
    };
    req.onsuccess = () => {
      idbReady = true;
      resolve(req.result);
    };
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

async function persistEntry(entry) {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(entry);
  } catch {
    // non-blocking
  }
}

async function loadSessionFromIdb(sid) {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(DB_STORE, 'readonly');
      const index = tx.objectStore(DB_STORE).index('sessionId');
      const req = index.getAll(sid);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

function buildEntry(partial) {
  const { path, queryKeys } = splitPathQuery(partial.path);
  return {
    id: uuid(),
    sessionId,
    sequence: ++sequence,
    timestamp: new Date().toISOString(),
    host: partial.host || null,
    method: partial.method || 'GET',
    path,
    queryKeys,
    userRole: context.userRole,
    userId: context.userId,
    activeTab: context.activeTab,
    callerTag: partial.callerTag || context.callerTag || null,
    callerStack: partial.callerStack || captureCallerStack(),
    outcome: partial.outcome || 'network',
    status: partial.status ?? null,
    durationMs: partial.durationMs ?? null,
    retried: Boolean(partial.retried),
    deduped: Boolean(partial.deduped),
    cacheHit: Boolean(partial.cacheHit),
    errorMessage: partial.errorMessage || null,
    responseBytes: partial.responseBytes ?? null,
    rateLimited: partial.status === 429,
    flushed: false,
  };
}

export function initSession(forceNew = false) {
  if (!forceNew && sessionId) return sessionId;
  const stored = sessionStorage.getItem('mentor_api_log_session');
  if (!forceNew && stored) {
    sessionId = stored;
    return sessionId;
  }
  sessionId = uuid();
  sessionStorage.setItem('mentor_api_log_session', sessionId);
  sequence = 0;
  ring = [];
  return sessionId;
}

export async function restoreFromIndexedDB() {
  if (!sessionId) initSession();
  const rows = await loadSessionFromIdb(sessionId);
  if (!rows.length) return;
  ring = rows
    .sort((a, b) => a.sequence - b.sequence)
    .slice(-RING_MAX);
  sequence = ring.reduce((max, row) => Math.max(max, row.sequence || 0), 0);
  notify();
}

export function setContext(patch = {}) {
  context = { ...context, ...patch };
}

export function setCallerTag(tag) {
  context.callerTag = tag || null;
}

export function setFlushHandler(handler) {
  flushHandler = typeof handler === 'function' ? handler : null;
}

export function record(partial) {
  if (!sessionId) initSession();
  const entry = buildEntry(partial);
  ring.push(entry);
  if (ring.length > RING_MAX) {
    ring = ring.slice(-RING_MAX);
  }
  persistEntry(entry);
  notify();
  return entry;
}

export function recordCacheHit({ baseUrl, path, method = 'GET', callerTag = null }) {
  return record({
    host: parseHost(baseUrl),
    method,
    path,
    outcome: 'cache_hit',
    cacheHit: true,
    durationMs: 0,
    callerTag,
  });
}

export function recordDeduped({ baseUrl, path, method = 'GET', callerTag = null }) {
  return record({
    host: parseHost(baseUrl),
    method,
    path,
    outcome: 'deduped',
    deduped: true,
    durationMs: 0,
    callerTag,
  });
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getEntries(filters = {}) {
  let rows = [...ring];
  if (filters.host) rows = rows.filter((r) => r.host === filters.host);
  if (filters.errorsOnly) rows = rows.filter((r) => r.errorMessage || (r.status != null && r.status >= 400));
  if (filters.duplicatesOnly) {
    const dupIds = new Set(findDuplicates(rows).map((d) => d.id));
    rows = rows.filter((r) => dupIds.has(r.id));
  }
  if (filters.pathSearch) {
    const q = filters.pathSearch.toLowerCase();
    rows = rows.filter((r) => r.path.toLowerCase().includes(q) || (r.callerTag || '').toLowerCase().includes(q));
  }
  if (filters.callerTag) rows = rows.filter((r) => r.callerTag === filters.callerTag);
  return rows.sort((a, b) => b.sequence - a.sequence);
}

export function summarize(entries = ring) {
  const list = entries || ring;
  return {
    total: list.length,
    network: list.filter((r) => r.outcome === 'network').length,
    failures: list.filter((r) => r.errorMessage || (r.status != null && r.status >= 400)).length,
    rateLimited: list.filter((r) => r.rateLimited).length,
    deduped: list.filter((r) => r.deduped || r.outcome === 'deduped').length,
    cacheHits: list.filter((r) => r.cacheHit || r.outcome === 'cache_hit').length,
    duplicates: findDuplicates(list).length,
  };
}

export function findDuplicates(entries = ring, windowMs = 2000) {
  const dups = [];
  const recent = [];
  const sorted = [...entries].sort((a, b) => a.sequence - b.sequence);
  sorted.forEach((entry) => {
    if (entry.outcome !== 'network') return;
    const ts = new Date(entry.timestamp).getTime();
    const key = `${entry.host}|${entry.method}|${entry.path}`;
    const prior = recent.filter((r) => r.key === key && ts - r.ts <= windowMs);
    if (prior.length > 0) {
      dups.push(entry);
    }
    recent.push({ key, ts, id: entry.id });
  });
  return dups;
}

export function isDuplicateEntry(entry, entries = ring, windowMs = 2000) {
  return findDuplicates(entries, windowMs).some((d) => d.id === entry.id);
}

export async function clearLocal() {
  ring = [];
  sequence = 0;
  const db = await openDb();
  if (db && sessionId) {
    try {
      const tx = db.transaction(DB_STORE, 'readwrite');
      const store = tx.objectStore(DB_STORE);
      const index = store.index('sessionId');
      const req = index.getAllKeys(sessionId);
      req.onsuccess = () => {
        (req.result || []).forEach((key) => store.delete(key));
      };
    } catch {
      // ignore
    }
  }
  notify();
}

export function exportJson(entries = getEntries()) {
  return JSON.stringify(entries, null, 2);
}

export function exportCsv(entries = getEntries()) {
  const cols = ['sequence', 'timestamp', 'host', 'method', 'path', 'status', 'durationMs', 'outcome', 'callerTag', 'errorMessage'];
  const lines = [cols.join(',')];
  entries.forEach((row) => {
    lines.push(cols.map((c) => {
      const val = row[c] ?? '';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    }).join(','));
  });
  return lines.join('\n');
}

function serializeForServer(entry) {
  return {
    client_id: entry.id,
    session_id: entry.sessionId,
    sequence: entry.sequence,
    user_id: entry.userId,
    user_role: entry.userRole,
    host: entry.host,
    method: entry.method,
    path: entry.path,
    query_keys: entry.queryKeys,
    caller_tag: entry.callerTag,
    caller_stack: entry.callerStack,
    active_tab: entry.activeTab,
    outcome: entry.outcome,
    status: entry.status,
    duration_ms: entry.durationMs,
    retried: entry.retried,
    deduped: entry.deduped,
    cache_hit: entry.cacheHit,
    error_message: entry.errorMessage,
    rate_limited: entry.rateLimited,
    logged_at: entry.timestamp,
  };
}

export async function flushToServer() {
  if (!flushHandler) return { ok: false, reason: 'no_handler' };
  const pending = ring.filter((r) => !r.flushed).slice(0, FLUSH_BATCH_MAX);
  if (!pending.length) return { ok: true, sent: 0 };
  try {
    await flushHandler(pending.map(serializeForServer));
    pending.forEach((row) => {
      row.flushed = true;
      persistEntry(row);
    });
    return { ok: true, sent: pending.length };
  } catch {
    return { ok: false, sent: 0 };
  }
}

export function startFlushTimer() {
  stopFlushTimer();
  flushTimer = setInterval(() => {
    flushToServer();
  }, FLUSH_INTERVAL_MS);
}

export function stopFlushTimer() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

export async function shutdown() {
  stopFlushTimer();
  await flushToServer();
}

export function getSessionId() {
  return sessionId;
}

export function isIdbReady() {
  return idbReady;
}